// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { Button, GridItem, Link, LoadingSpinner } from "@insite/mobius/index";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CutEdgeContainer from "@stanion/App/Components/CutEdge/CutEdge";
import TitleTypography from "@stanion/App/Components/TitleTypography/TitleTypography";
import XngCarousel from "@stanion/App/Components/XngCarousel/XngCarousel";
import xLoadAllEvents from "@stanion/App/Handlers/AllEvents/AllEventsHandler";
import { AllEventsApiParameter } from "@stanion/App/Services/AllEventsService";
import dataTableSharedStyles from "@stanion/App/SharedStyle/MyAccount/List/DataTableSharedStyles";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { getAllEventsDataView } from "@stanion/App/Store/Selectors/xAllEventsRequestSelector";
import EventCalendar from "@stanion/XDL/Icons/EventCalendar";
import EventLocation from "@stanion/XDL/Icons/EventLocation";
import { ellipsis, formatDateTime, xScreen } from "@stanion/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    return {
        upcommingEvents: getAllEventsDataView(state, upcomingEventsParameter),
        allEventsPageUrl: getPageLinkByPageType(state, "AllEventsPage")?.url,
        getAllEventsParameter: state.xAllEventsPage.getAllEventsParameter,
        language: state.context.session.language,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    xLoadAllEvents,
};

export interface UpcommingEventsStyles {
    wrapper?: InjectableCss;
    slide?: InjectableCss;
    titleText?: TypographyProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    slideContainer?: InjectableCss;
    dateContainer?: InjectableCss;
    dateText?: TypographyProps;
    addressText?: TypographyProps;
    addressTextContainer?: InjectableCss;
    slideTabledContainer?: InjectableCss;
    mobileSlide?: InjectableCss;
    registerButton?: ButtonPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const upcommingEventsStyles: UpcommingEventsStyles = {
    slideContainer: {
        css: css`
            display: flex;
            align-items: center;
            padding-left: 15px;
            padding-right: 15px;
            column-gap: 50px;
            > div {
                max-width: 400px;
            }
        `,
    },
    slideTabledContainer: {
        css: css`
            .swiper-slide {
                ${xScreen("lte", "md")} {
                    max-width: 46%;
                }

                ${xScreen("lte", "xs")} {
                    max-width: 100%;
                }
            }
        `,
    },
    titleText: {
        color: "primary",
        size: 14,
        lineHeight: "20px",
        weight: "bold",
        css: css`
            display: block;
            height: 40px;
            ${ellipsis(2)}
        `,
    },
    noResultsContainer: {
        ...dataTableSharedStyles.noResultsContainer,
    },
    noResultsText: {
        ...dataTableSharedStyles.noResultsText,
    },
    spinner: {
        ...dataTableSharedStyles.spinner,
    },
    centeringWrapper: {
        ...dataTableSharedStyles.centeringWrapper,
    },
    dateText: {
        weight: "bold",
        size: 12,
        lineHeight: "17px",
    },
    addressText: {
        size: 12,
        lineHeight: "17px",
        css: css`
            margin-right: auto;
        `,
    },
    dateContainer: {
        css: css`
            margin-top: 16px;
            margin-bottom: 11px;
            display: flex;
            align-items: flex-start;
        `,
    },
    addressTextContainer: {
        css: css`
            margin-top: 16px;
            margin-bottom: 11px;
            display: flex;
            align-items: flex-start;
        `,
    },
    registerButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            border-radius: 2px;
        `,
    },
};

const styles = upcommingEventsStyles;

const upcomingEventsParameter: AllEventsApiParameter = {
    page: 1,
    pageSize: 3,
};

const UpcommingEvents: FC<Props> = ({
    xLoadAllEvents,
    upcommingEvents,
    allEventsPageUrl,
    language,
    isTabletScreen,
}) => {
    const history = useHistory();
    useEffect(() => {
        if (!upcommingEvents.value && !upcommingEvents.isLoading) {
            xLoadAllEvents(upcomingEventsParameter);
        }
    }, [upcommingEvents.value]);

    if (upcommingEvents.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!upcommingEvents.value) {
        return null;
    }

    if (upcommingEvents?.value.length === 0) {
        return null;
    }

    const handleNavigation = (url?: string) => {
        if (!url) {
            return;
        }
        history.push(url);
    };

    const upcommingSlides = upcommingEvents.value?.map(item => {
        const startTime = formatDateTime(new Date(item.eventStartDate));
        const endTime = formatDateTime(new Date(item.eventEndDate));
        const startDate = getLocalizedDateTime({
            dateTime: new Date(item.eventStartDate),
            language,
            options: {
                month: "short",
                day: "numeric",
            },
        });
        const endDate = getLocalizedDateTime({
            dateTime: new Date(item.eventEndDate),
            language,
            options: {
                month: "short",
                day: "numeric",
            },
        });
        return (
            <GridItem width={12} key={item.id}>
                <CutEdgeContainer
                    edgeSize="16px"
                    borderWidth="1px"
                    borderColor="common.ui4"
                    backgroundColor="primary.contrast"
                    customWidth="100%"
                    padding="16px"
                    oneCuttingEdge={true}
                >
                    <Link href={item.url}>
                        <Typography {...styles.titleText}>{item.eventTitle}</Typography>
                    </Link>
                    <StyledWrapper {...styles.dateContainer}>
                        <Link href={item.url}>
                            <ButtonIcon
                                src={EventCalendar}
                                size={16}
                                css={css`
                                    margin-right: 8px;
                                `}
                            />
                        </Link>

                        <Typography {...styles.dateText}>
                            {startDate === endDate ? startDate : `${startDate} - ${endDate}`}
                            {` @ ${startTime} - ${endTime}`}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.addressTextContainer}>
                        <ButtonIcon
                            src={EventLocation}
                            size={16}
                            css={css`
                                margin-right: 8px;
                            `}
                        />
                        <Typography {...styles.addressText}>
                            {item.city} - {item.state} - {item.postalCode}
                        </Typography>
                        <Button onClick={() => handleNavigation(item.url)} {...styles.registerButton}>
                            {translate("Register")}
                        </Button>
                    </StyledWrapper>
                </CutEdgeContainer>
            </GridItem>
        );
    });
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="UpcommingEventsWidget">
            <TitleTypography
                linkText={translate("View All Events")}
                link={allEventsPageUrl}
                data-test-selector="UpcommingEventsTitle"
            >
                {translate("Upcoming Events")}
            </TitleTypography>

            <StyledWrapper data-test-selector="slides">
                {isTabletScreen ? (
                    <StyledWrapper {...styles.slideTabledContainer}>
                        <XngCarousel spaceBetween={50} slidesPerView="auto">
                            {upcommingSlides}
                        </XngCarousel>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.slideContainer}>{upcommingSlides}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(UpcommingEvents)),
    definition: {
        group: "Upcomming Events" as any,
        isSystem: true,
        fieldDefinitions: [],
    },
};

export default widgetModule;
